import { useAuth } from '@/components/auth/authProvider';
import RequirePalmettoFinanceUser, { isPalmettoFinanceUser } from '@/components/auth/RequirePalmettoFinanceUser';
import RequirePermissions from '@/components/auth/requirePermissions';
import { getStateOptions } from '@/helpers/getStateOptions';
import { useEditOrganizationMutation, useGetOrganizationDetailsQuery } from '@/services/organizations';
import { Badge, Box, Button, SelectInputNative, Table, TextInput, toast } from '@palmetto/palmetto-components';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ProgramType, ProgramTypeLabels, StateLicenseNumber, StateLicenseStatus, UserPermissions } from 'types';

export const OrganizationStateLicenses = () => {
  const [searchParams] = useSearchParams();
  const programType = searchParams.get('programType');
  const params = useParams<{ alias: string }>();
  const { claims } = useAuth();
  const isFincoUser = isPalmettoFinanceUser(claims);
  const { alias } = params;
  const navigate = useNavigate();
  const { data: organization } = useGetOrganizationDetailsQuery({ alias: alias as string });
  const [update] = useEditOrganizationMutation();
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [addingRowId, setAddingRowId] = useState<number | null>(null);
  const [editedValues, setEditedValues] = useState<Partial<StateLicenseNumber>>({});
  const [pendingLicense, setPendingLicense] = useState<StateLicenseNumber | null>(null);

  // Derive filtered licenses directly from organization data
  const filteredLicenses = useMemo(() => {
    if (!organization?.stateLicenseNumbers) return [];
    return organization.stateLicenseNumbers
      .filter((license) => license.programType === programType)
      .toSorted((a, b) => a.state.localeCompare(b.state));
  }, [organization?.stateLicenseNumbers, programType]);

  // Combine filtered licenses with pending license for display
  const licenses = useMemo(() => {
    if (pendingLicense && pendingLicense.programType === programType) {
      return [...filteredLicenses, pendingLicense];
    }
    return filteredLicenses;
  }, [filteredLicenses, pendingLicense, programType]);

  // Default to first program type if none selected
  useEffect(() => {
    const programTypes = organization?.programTypes;
    if (programTypes?.length && !programTypes.includes(programType as ProgramType)) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('programType', programTypes[0]);
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  }, [organization?.programTypes, programType, searchParams, navigate]);

  // Reset editing state when program type changes
  useEffect(() => {
    setEditingRowId(null);
    setAddingRowId(null);
    setEditedValues({});
    setPendingLicense(null);
  }, [programType]);

  const programTypesOptions = organization?.programTypes?.map((pt: ProgramType) => ({
    label: ProgramTypeLabels[pt],
    value: pt,
  }));

  const handleProgramTypeChange = (event: any) => {
    const newProgramType = event.target.value ?? '';
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('programType', newProgramType);
    navigate({ search: newSearchParams.toString() });
  };

  const handleEdit = (rowIndex: number) => {
    setEditingRowId(rowIndex);
    setEditedValues({ ...licenses[rowIndex] });
  };

  const handleSave = async () => {
    if (editingRowId !== null || addingRowId !== null) {
      const rowId = editingRowId !== null ? editingRowId : addingRowId!;
      if (!editedValues.state || !editedValues.contractorLicenseNumber) {
        toast.error('State and Contractor License Number are required.');
        return;
      }
      const updatedLicenses = licenses.map((license, index) =>
        index === rowId ? { ...license, ...editedValues } : license,
      );
      const otherProgramLicenses = organization?.stateLicenseNumbers?.filter(
        (license) => license.programType !== programType,
      );
      setEditingRowId(null);
      setAddingRowId(null);
      setEditedValues({});
      setPendingLicense(null);

      try {
        await update({
          organization: {
            licenses: [...updatedLicenses, ...(otherProgramLicenses || [])],
          },
          alias: alias as string,
        }).unwrap();
        toast.success('License saved successfully.');
      } catch (error) {
        console.error(error);
        toast.error('Failed to save license. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    setEditingRowId(null);
    setAddingRowId(null);
    setEditedValues({});
    setPendingLicense(null);
  };

  const handleDelete = async (rowIndex: number) => {
    const license = filteredLicenses[rowIndex];
    const confirmDelete = window.confirm(
      `Are you sure you want to delete the license for ${license.state}? This action cannot be undone.`,
    );
    if (confirmDelete) {
      const otherProgramLicenses = organization?.stateLicenseNumbers?.filter((l) => l.programType !== programType);
      const updatedLicenses = filteredLicenses.filter((_, index) => index !== rowIndex);
      try {
        await update({
          organization: {
            licenses: [...updatedLicenses, ...(otherProgramLicenses || [])],
          },
          alias: alias as string,
        }).unwrap();
        toast.success('License deleted successfully.');
      } catch (error) {
        console.error(error);
        toast.error('Failed to delete license. Please try again.');
      }
    }
  };

  const handleInputChange = (field: keyof StateLicenseNumber, value: string) => {
    setEditedValues((prev) => ({ ...prev, [field]: value }));
  };

  const stateOptions = useMemo(() => {
    return getStateOptions(true).filter((option) => !licenses.some((license) => license.state === option.value));
  }, [licenses]);

  const handleAddNew = () => {
    const newLicense: StateLicenseNumber = {
      state: '',
      contractorLicenseNumber: '',
      additionalLicenseNumbers: '',
      programType: programType as ProgramType,
      status: StateLicenseStatus.active,
    };
    setPendingLicense(newLicense);
    setAddingRowId(filteredLicenses.length);
    setEditedValues(newLicense);
  };

  const stateLicenseColumns = [
    {
      heading: 'Status',
      dataKey: 'status',
      render: (cell: any, _row: any, rowIndex?: number) => {
        if (editingRowId === rowIndex) {
          return (
            <SelectInputNative
              id={`status-${rowIndex}`}
              label=""
              value={editedValues.status || cell}
              onChange={(e) => handleInputChange('status', e.target.value)}
              options={[
                { label: 'Active', value: StateLicenseStatus.active },
                { label: 'Inactive', value: StateLicenseStatus.inactive },
                { label: 'Suspended', value: StateLicenseStatus.suspended },
              ]}
              size="sm"
            />
          );
        }
        return (
          <Badge
            variant={
              cell === StateLicenseStatus.active
                ? 'success'
                : cell === StateLicenseStatus.inactive
                  ? 'warning'
                  : 'danger'
            }
            message={cell}
          />
        );
      },
    },
    {
      heading: 'State',
      dataKey: 'state',
      render: (cell: any, _row: any, rowIndex?: number) => {
        if (addingRowId === rowIndex) {
          return (
            <SelectInputNative
              id={`state-${rowIndex}`}
              label=""
              value={editedValues.state || ''}
              onChange={(e) => handleInputChange('state', e.target.value)}
              options={stateOptions}
              size="sm"
            />
          );
        }
        return (
          <Box fontWeight="medium" as="span" display="inline">
            {cell}
          </Box>
        );
      },
    },
    {
      heading: 'License Number',
      dataKey: 'contractorLicenseNumber',
      render: (cell: any, _row: any, rowIndex?: number) => {
        if (editingRowId === rowIndex || addingRowId === rowIndex) {
          return (
            <TextInput
              id={`license-${rowIndex}`}
              label=""
              value={editedValues.contractorLicenseNumber}
              onChange={(e) => handleInputChange('contractorLicenseNumber', e.target.value)}
              size="sm"
            />
          );
        }
        return cell;
      },
    },
    {
      heading: 'Monetary Limit',
      dataKey: 'monetaryLimit',
      render: (cell: any, _row: any, rowIndex?: number) => {
        if (editingRowId === rowIndex || addingRowId === rowIndex) {
          return (
            <TextInput
              id={`monetary-${rowIndex}`}
              label=""
              value={editedValues.monetaryLimit?.toString() || ''}
              onChange={(e) => handleInputChange('monetaryLimit', e.target.value)}
              size="sm"
            />
          );
        }
        return cell;
      },
    },
    {
      heading: 'Additional License Numbers',
      dataKey: 'additionalLicenseNumbers',
      render: (cell: any, _row: any, rowIndex?: number) => {
        if (editingRowId === rowIndex || addingRowId === rowIndex) {
          return (
            <TextInput
              id={`additional-${rowIndex}`}
              label=""
              value={editedValues.additionalLicenseNumbers || ''}
              onChange={(e) => handleInputChange('additionalLicenseNumbers', e.target.value)}
              size="sm"
            />
          );
        }
        return cell;
      },
    },
    ...(isFincoUser
      ? [
          {
            heading: 'Actions',
            render: (_cell: any, _row: any, rowIndex?: number) => {
              if (editingRowId === rowIndex || addingRowId === rowIndex) {
                return (
                  <Box direction="row" gap="xs">
                    <Button variant="primary" size="sm" onClick={handleSave}>
                      Save
                    </Button>
                    <Button variant="secondary" size="sm" onClick={handleCancel}>
                      Cancel
                    </Button>
                  </Box>
                );
              }
              return (
                <RequirePermissions permissions={[UserPermissions.admin]}>
                  <Box direction="row" gap="xs">
                    <Button
                      variant="tertiary"
                      size="sm"
                      tone="neutral"
                      onClick={() => handleEdit(rowIndex ?? 0)}
                      iconPrefix="pencil"
                    />
                    <Button
                      variant="tertiary"
                      size="sm"
                      tone="neutral"
                      onClick={() => handleDelete(rowIndex ?? 0)}
                      iconPrefix="trash"
                    />
                  </Box>
                </RequirePermissions>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <Box margin="lg 0 0 0" childGap="md">
      <Box as="h4" fontSize="md" fontWeight="medium">
        State Licenses
      </Box>
      <Box direction="row" alignItems="center" justifyContent="space-between" gap="md">
        <Box direction="row" alignItems="center" gap="md">
          {organization?.programTypes?.length && organization?.programTypes?.length > 1 && (
            <Box maxWidth="2xl">
              <SelectInputNative
                id="required"
                label="Program Type"
                value={programType || null}
                onChange={handleProgramTypeChange}
                options={programTypesOptions || []}
                menuPortalTarget={document.body}
              />
            </Box>
          )}
        </Box>
        <RequirePalmettoFinanceUser>
          <RequirePermissions permissions={[UserPermissions.admin]}>
            <Button variant="primary" size="sm" iconPrefix="add" onClick={handleAddNew}>
              Add License
            </Button>
          </RequirePermissions>
        </RequirePalmettoFinanceUser>
      </Box>
      {licenses.length ? (
        <Table
          key={programType}
          isCompact
          rowKey="contractorLicenseNumber"
          columns={stateLicenseColumns}
          rows={licenses}
          isScrollable={{
            x: true,
          }}
        />
      ) : (
        'No licenses on file'
      )}
    </Box>
  );
};
